import React from 'react';

export interface UseControlledProps<T> {
  /**
   * The controlled value. When `undefined`, the hook is uncontrolled.
   */
  controlled: T | undefined;
  /**
   * The default (initial) value used in uncontrolled mode.
   */
  default: T | undefined;
  /**
   * The component name, used in development-only warnings about controlled/uncontrolled toggling.
   */
  name?: string;
  /**
   * The state name shown in development-only warnings (defaults to `'value'`).
   */
  state?: string;
}

/**
 * `useControlled` lets a component support both controlled and uncontrolled modes
 * with a single `[value, setValue]` API.
 *
 * Mirrors the shape of `@mui/utils/useControlled` so MUI-derived hooks port cleanly.
 *
 * - When `controlled` is defined, the hook always returns the controlled value and
 *   `setValue` becomes a no-op for state but is still safe to call.
 * - When `controlled` is `undefined`, internal `useState` tracks the value seeded by `default`.
 */
export const useControlled = <T>({
  controlled,
  default: defaultValue,
}: UseControlledProps<T>): [T, (newValue: T) => void] => {
  const isControlled = React.useRef(controlled !== undefined).current;
  const [valueState, setValue] = React.useState<T>(defaultValue as T);
  const value = (isControlled ? controlled : valueState) as T;

  const setValueIfUncontrolled = React.useCallback(
    (newValue: T) => {
      if (!isControlled) {
        setValue(newValue);
      }
    },
    [isControlled]
  );

  return [value, setValueIfUncontrolled];
};
